import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Mail, Copy, Check } from "lucide-react";
import { useTranslation } from "../i18n/useTranslation";

const SUPPORT_EMAIL = "support@prayergroup.site";

/**
 * A tiny help dialog that exposes the support email with mailto + copy-to-clipboard.
 * Kept self-contained so any page with a user menu can drop it in.
 *
 * Props:
 *   open: boolean
 *   onOpenChange: (open: boolean) => void
 */
export function HelpDialog({ open, onOpenChange }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
    } catch {
      // Fallback for older browsers / insecure contexts
      const ta = document.createElement("textarea");
      ta.value = SUPPORT_EMAIL;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="help-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">{t("help.title")}</DialogTitle>
          <DialogDescription>{t("help.description")}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col sm:flex-row gap-2 items-center justify-center py-2">
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-white rounded-full px-6 py-3 text-sm font-medium transition-colors"
            data-testid="help-email-link"
          >
            <Mail className="w-4 h-4" />
            {SUPPORT_EMAIL}
          </a>
          <button
            type="button"
            onClick={copyEmail}
            aria-live="polite"
            className="inline-flex items-center gap-2 border border-sage/40 text-sage hover:bg-sage/10 rounded-full px-4 py-3 text-sm font-medium transition-colors"
            data-testid="help-email-copy"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? t("help.copied") : t("help.copy")}
          </button>
        </div>

        <p className="text-xs text-stone-dark/50 dark:text-stone-light/50 text-center">
          {t("help.replyTime")}
        </p>

        <DialogFooter>
          <Button variant="outline" className="rounded-full w-full sm:w-auto" onClick={() => onOpenChange(false)}>
            {t("common.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
